import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import Button from "../button";
import Modal from "../modal";
import useOutsideClick from "../../hooks/useOutsideClick";
import { useSavedFilterTemplates } from "../../hooks/useSavedFilterTemplates";
import trashIconDark from "../../images/ic_bin.svg";
import trashIconLight from "../../images/trash_blue.svg";
import {
  PanelToggleButton,
  TemplateControls,
  TemplateControlsSection,
  TemplateDropdown,
  TemplateDropdownButton,
  TemplateDropdownButtonText,
  TemplateDropdownApplyButton,
  TemplateDropdownDeleteButton,
  TemplateDropdownEmptyState,
  TemplateDropdownItem,
  TemplateDropdownItemMeta,
  TemplateDropdownItemTop,
  TemplateDropdownItemTitle,
  TemplateDropdownMenu,
  TemplateDropdownRetryButton,
  TemplateModalActions,
  TemplateModalText,
  TemplateModalTitle,
  TemplateSaveModalActions,
  TemplateSaveModalBody,
  TemplateSaveModalCancelButton,
  TemplateSaveModalCount,
  TemplateSaveModalError,
  TemplateSaveModalField,
  TemplateSaveModalFieldFooter,
  TemplateSaveModalHeader,
  TemplateSaveModalInput,
  TemplateSaveModalLabel,
  TemplateSaveModalText,
  TemplateSaveModalTitle,
  TemplateSecondaryButton,
  TemplateSelectedBadge,
  TemplateStatusMessage,
} from "../../style/CompareWorkspaceStyle";

export const SavedFilterTemplatesControlsView = ({
  accessDenied,
  deleteModalOpen,
  dropdownOpen,
  isDeletingTemplate,
  isLoadingTemplates,
  isSavingTemplate,
  onAccessDeniedChange,
  pageKey,
  pendingDeleteTemplate,
  refreshTemplates,
  saveErrorMessage,
  saveModalOpen,
  saveMode,
  selectedTemplate,
  setDropdownOpen,
  statusMessage,
  templateTitle,
  templateTitleMaxLength,
  templates,
  templatesError,
  titleError,
  handleApplySelectedTemplate,
  handleCloseDeleteModal,
  handleCloseSaveModal,
  handleDeleteTemplate,
  handleDropdownToggle,
  handleOpenCreateModal,
  handleOpenDeleteModal,
  handleOpenUpdateModal,
  handleSaveTemplate,
  handleTemplateTitleChange,
  showCreateButton = true,
  showDropdown = true,
  showUpdateButton = true,
  showStatusMessage = true,
  showModals = true,
}) => {
  const { t } = useTranslation();
  const dropdownRef = useOutsideClick(() => {
    if (dropdownOpen) {
      setDropdownOpen(false);
    }
  });

  const trashIcon = useMemo(
    () =>
      localStorage.getItem("theme") === "light"
        ? trashIconLight
        : trashIconDark,
    []
  );

  useEffect(() => {
    onAccessDeniedChange?.(accessDenied);
  }, [accessDenied, onAccessDeniedChange]);

  const templateTitleFieldId = `${pageKey}-saved-template-title`;
  const templateTitleCountId = `${templateTitleFieldId}-count`;
  const templateTitleError = titleError || saveErrorMessage;
  const templateTitleErrorId = templateTitleError
    ? `${templateTitleFieldId}-error`
    : undefined;
  const templateTitleDescribedBy = [templateTitleErrorId, templateTitleCountId]
    .filter(Boolean)
    .join(" ");

  const shouldRenderControls =
    showCreateButton || showDropdown || (showUpdateButton && selectedTemplate);

  return (
    <>
      {(shouldRenderControls || (showStatusMessage && statusMessage?.text)) && (
        <TemplateControlsSection>
          {shouldRenderControls && (
            <TemplateControls>
              {showCreateButton ? (
                <PanelToggleButton type="button" onClick={handleOpenCreateModal}>
                  {t("savedTemplates.save")}
                </PanelToggleButton>
              ) : null}

              {showDropdown ? (
                <TemplateDropdown ref={dropdownRef}>
                  <TemplateDropdownButton
                    type="button"
                    $active={dropdownOpen}
                    aria-expanded={dropdownOpen}
                    onClick={handleDropdownToggle}
                  >
                    <TemplateDropdownButtonText>
                      <span>{t("savedTemplates.savedTemplates")}</span>
                      {selectedTemplate?.title ? (
                        <strong>{selectedTemplate.title}</strong>
                      ) : null}
                    </TemplateDropdownButtonText>
                  </TemplateDropdownButton>

                  {dropdownOpen && (
                    <TemplateDropdownMenu>
                      {isLoadingTemplates ? (
                        <TemplateDropdownEmptyState>
                          {t("savedTemplates.loading")}
                        </TemplateDropdownEmptyState>
                      ) : templatesError ? (
                        <>
                          <TemplateDropdownEmptyState>
                            {templatesError}
                          </TemplateDropdownEmptyState>
                          <TemplateDropdownRetryButton
                            type="button"
                            onClick={() => refreshTemplates()}
                          >
                            {t("savedTemplates.retry")}
                          </TemplateDropdownRetryButton>
                        </>
                      ) : templates.length ? (
                        templates.map((template) => {
                          const isSelected = selectedTemplate?.id === template?.id;

                          return (
                            <TemplateDropdownItem
                              key={template.id}
                              $active={isSelected}
                            >
                              <TemplateDropdownApplyButton
                                type="button"
                                onClick={() =>
                                  handleApplySelectedTemplate(template)
                                }
                              >
                                <TemplateDropdownItemTop>
                                  <TemplateDropdownItemTitle>
                                    {template.title}
                                  </TemplateDropdownItemTitle>
                                  {isSelected ? (
                                    <TemplateSelectedBadge>
                                      {t("savedTemplates.selected")}
                                    </TemplateSelectedBadge>
                                  ) : null}
                                </TemplateDropdownItemTop>

                                <TemplateDropdownItemMeta>
                                  {t("savedTemplates.applyTemplate")}
                                </TemplateDropdownItemMeta>
                              </TemplateDropdownApplyButton>

                              <TemplateDropdownDeleteButton
                                type="button"
                                title={t("savedTemplates.delete")}
                                aria-label={`${t("savedTemplates.delete")} ${template.title}`}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleOpenDeleteModal(template);
                                }}
                              >
                                <img
                                  src={trashIcon}
                                  alt=""
                                  aria-hidden="true"
                                />
                              </TemplateDropdownDeleteButton>
                            </TemplateDropdownItem>
                          );
                        })
                      ) : (
                        <TemplateDropdownEmptyState>
                          {t("savedTemplates.empty")}
                        </TemplateDropdownEmptyState>
                      )}
                    </TemplateDropdownMenu>
                  )}
                </TemplateDropdown>
              ) : null}

              {showUpdateButton && selectedTemplate ? (
                <PanelToggleButton type="button" onClick={handleOpenUpdateModal}>
                  {t("savedTemplates.update")}
                </PanelToggleButton>
              ) : null}
            </TemplateControls>
          )}

          {showStatusMessage && statusMessage?.text ? (
            <TemplateStatusMessage $type={statusMessage.type}>
              {statusMessage.text}
            </TemplateStatusMessage>
          ) : null}
        </TemplateControlsSection>
      )}

      {showModals ? (
        <>
          <Modal
            isOpen={saveModalOpen}
            setIsOpen={handleCloseSaveModal}
            closeIc
            width="760px"
            mobileWidth="calc(100% - 24px)"
            padding="72px 64px 48px"
            mobilePadding="64px 20px 32px"
            contentWidth="100%"
            contentMobileWidth="100%"
            contentTextAlign="left"
            unstyledTypography
          >
            <TemplateSaveModalBody>
              <TemplateSaveModalHeader>
                <TemplateSaveModalTitle>
                  {saveMode === "update"
                    ? t("savedTemplates.updateTitle")
                    : t("savedTemplates.saveTitle")}
                </TemplateSaveModalTitle>
                <TemplateSaveModalText>
                  {saveMode === "update"
                    ? t("savedTemplates.updateDescription")
                    : t("savedTemplates.saveDescription")}
                </TemplateSaveModalText>
              </TemplateSaveModalHeader>

              <TemplateSaveModalField>
                <TemplateSaveModalLabel htmlFor={templateTitleFieldId}>
                  {t("savedTemplates.templateName")}
                </TemplateSaveModalLabel>
                <TemplateSaveModalInput
                  id={templateTitleFieldId}
                  type="text"
                  value={templateTitle}
                  placeholder={t("savedTemplates.templateNamePlaceholder")}
                  onChange={(event) =>
                    handleTemplateTitleChange(event.target.value)
                  }
                  maxLength={templateTitleMaxLength}
                  autoFocus
                  disabled={isSavingTemplate}
                  aria-invalid={Boolean(templateTitleError)}
                  aria-describedby={templateTitleDescribedBy || undefined}
                  $hasError={Boolean(templateTitleError)}
                />
                <TemplateSaveModalFieldFooter
                  $hasError={Boolean(templateTitleError)}
                >
                  {templateTitleError ? (
                    <TemplateSaveModalError id={templateTitleErrorId}>
                      {templateTitleError}
                    </TemplateSaveModalError>
                  ) : null}
                  <TemplateSaveModalCount id={templateTitleCountId}>
                    {templateTitle.length}/{templateTitleMaxLength}
                  </TemplateSaveModalCount>
                </TemplateSaveModalFieldFooter>
              </TemplateSaveModalField>

              <TemplateSaveModalActions>
                <Button
                  title={
                    isSavingTemplate
                      ? t("savedTemplates.saving")
                      : saveMode === "update"
                        ? t("savedTemplates.updateConfirm")
                        : t("savedTemplates.saveConfirm")
                  }
                  size="full"
                  onClick={handleSaveTemplate}
                  disabled={isSavingTemplate}
                />
                <TemplateSaveModalCancelButton
                  type="button"
                  onClick={handleCloseSaveModal}
                  disabled={isSavingTemplate}
                >
                  {t("savedTemplates.cancel")}
                </TemplateSaveModalCancelButton>
              </TemplateSaveModalActions>
            </TemplateSaveModalBody>
          </Modal>

          <Modal
            isOpen={deleteModalOpen}
            setIsOpen={handleCloseDeleteModal}
            closeIc
          >
            <TemplateModalTitle>
              {t("savedTemplates.deleteTitle")}
            </TemplateModalTitle>
            <TemplateModalText>
              {t("savedTemplates.deleteDescription", {
                title: pendingDeleteTemplate?.title || "",
              })}
            </TemplateModalText>

            <TemplateModalActions>
              <Button
                title={
                  isDeletingTemplate
                    ? t("savedTemplates.deleting")
                    : t("savedTemplates.deleteConfirm")
                }
                size="full"
                onClick={handleDeleteTemplate}
                disabled={isDeletingTemplate}
              />
              <TemplateSecondaryButton
                type="button"
                onClick={handleCloseDeleteModal}
                disabled={isDeletingTemplate}
              >
                {t("savedTemplates.cancel")}
              </TemplateSecondaryButton>
            </TemplateModalActions>
          </Modal>
        </>
      ) : null}
    </>
  );
};

const SavedFilterTemplatesControls = ({
  pageKey,
  filters,
  onApplyTemplate,
  onAccessDeniedChange,
}) => {
  const savedTemplateControls = useSavedFilterTemplates({
    pageKey,
    filters,
    onApplyTemplate,
  });

  return (
    <SavedFilterTemplatesControlsView
      {...savedTemplateControls}
      pageKey={pageKey}
      onAccessDeniedChange={onAccessDeniedChange}
    />
  );
};

export default SavedFilterTemplatesControls;
